// src/pages/ProjectDetailPage.jsx
//
// All 6 stage cards for one project, per PROJECT_TRACKER_PLAN.md's page
// split (ProjectListPage for identity, ProjectDetailPage for everything
// else). Project identity fields (name, proponent, type, award date) are
// editable inline the same way StageCard edits its own extra fields.
//
// Not built yet, deliberately -- FieldworkSession and Site are per-project
// sub-resources but not part of the 6-stage claim workflow, and per
// Jerome's note he wants to be more hands-on once we get to the deeper
// Site/Report sub-trackers. Skipping them here keeps this page scoped to
// what was actually asked for.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import Navbar from "../components/Navbar";
import StageCard from "../components/StageCard";
import { STATUS_CHOICES, FIELDWORK_STATUS_CHOICES } from "../constants";

const PROJECT_FIELDS = [
  { key: "project_name", label: "Name", type: "text" },
  { key: "proponent_name", label: "Proponent", type: "text" },
  { key: "award_date", label: "Award date", type: "date" },
];

export default function ProjectDetailPage() {
  const { projectId } = useParams();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [projectTypes, setProjectTypes] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [editTypeId, setEditTypeId] = useState("");
  const [saving, setSaving] = useState(false);

  const loadProject = () => {
    setLoading(true);
    setError("");
    apiFetch(`/tracker/projects/${projectId}/`)
      .then((data) => {
        setProject(data);
        const values = {};
        PROJECT_FIELDS.forEach((f) => {
          values[f.key] = data[f.key] || "";
        });
        setEditValues(values);
        setEditTypeId(data.project_type || "");
      })
      .catch((err) => setError(err.message || "Couldn't load project."))
      .finally(() => setLoading(false));
  };

  useEffect(loadProject, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    apiFetch("/tracker/project-types/")
      .then(setProjectTypes)
      .catch(() => setProjectTypes([]));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { project_type: editTypeId || null };
      PROJECT_FIELDS.forEach((f) => {
        payload[f.key] = editValues[f.key] || null;
      });
      const data = await apiFetch(`/tracker/projects/${projectId}/`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setProject(data);
      setEditing(false);
    } catch (err) {
      setError(err.message || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="page">
          <p>Loading...</p>
        </main>
      </>
    );
  }

  if (error && !project) {
    return (
      <>
        <Navbar />
        <main className="page">
          <p className="form-error">{error}</p>
        </main>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="page">
        <div className="page-header">
          <h1>{project.project_no}</h1>
        </div>

        <div className="card">
          {!editing && (
            <>
              <table className="fields-table">
                <tbody>
                  <tr>
                    <td className="fields-table-label">Name</td>
                    <td>{project.project_name || "--"}</td>
                  </tr>
                  <tr>
                    <td className="fields-table-label">Proponent</td>
                    <td>{project.proponent_name || "--"}</td>
                  </tr>
                  <tr>
                    <td className="fields-table-label">Type</td>
                    <td>{project.project_type_code || "--"}</td>
                  </tr>
                  <tr>
                    <td className="fields-table-label">Award date</td>
                    <td>{project.award_date || "--"}</td>
                  </tr>
                </tbody>
              </table>
              <button type="button" onClick={() => setEditing(true)}>
                Edit details
              </button>
            </>
          )}
          {editing && (
            <form onSubmit={handleSave} className="stacked-form">
              {PROJECT_FIELDS.map((f) => (
                <div key={f.key}>
                  <label>{f.label}</label>
                  <input
                    type={f.type}
                    value={editValues[f.key]}
                    onChange={(e) =>
                      setEditValues((v) => ({ ...v, [f.key]: e.target.value }))
                    }
                  />
                </div>
              ))}
              <div>
                <label>Type</label>
                <select value={editTypeId} onChange={(e) => setEditTypeId(e.target.value)}>
                  <option value="">--</option>
                  {projectTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.code}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
                <button type="button" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          )}
          {error && <p className="form-error">{error}</p>}
        </div>

        <div className="stage-card-grid">
          <StageCard
            title="Permit"
            projectId={projectId}
            basePath="/tracker/permits"
            statusField="application_status"
            statusChoices={STATUS_CHOICES}
            extraFields={[
              { key: "permit_no", label: "Permit #", type: "text" },
              { key: "holder", label: "Holder", type: "text" },
              { key: "expiry_date", label: "Expiry date", type: "date" },
            ]}
            creatable
          />
          <StageCard
            title="Fieldwork"
            projectId={projectId}
            basePath="/tracker/fieldwork"
            statusField="status"
            statusChoices={FIELDWORK_STATUS_CHOICES}
          />
          <StageCard
            title="Site data"
            projectId={projectId}
            basePath="/tracker/site-data"
            statusField="status"
            statusChoices={STATUS_CHOICES}
            extraFields={[{ key: "due_date", label: "Due date", type: "date" }]}
            hasRevision
          />
          <StageCard
            title="Final report"
            projectId={projectId}
            basePath="/tracker/final-reports"
            statusField="status"
            statusChoices={STATUS_CHOICES}
            extraFields={[
              { key: "due_date", label: "Due date", type: "date" },
              { key: "approval_date", label: "Approval date", type: "date" },
            ]}
            hasRevision
          />
          <StageCard
            title="Digital data"
            projectId={projectId}
            basePath="/tracker/digital-data"
            statusField="status"
            statusChoices={STATUS_CHOICES}
            extraFields={[
              { key: "due_date", label: "Due date", type: "date" },
              { key: "approval_date", label: "Approval date", type: "date" },
            ]}
          />
          <StageCard
            title="Artifact submission"
            projectId={projectId}
            basePath="/tracker/artifact-submissions"
            statusField="status"
            statusChoices={STATUS_CHOICES}
            extraFields={[{ key: "due_date", label: "Due date", type: "date" }]}
          />
        </div>
      </main>
    </>
  );
}
